import { useNostr } from '@nostrify/react';
import { useQuery } from '@tanstack/react-query';
import type { NostrEvent } from '@nostrify/nostrify';

/** Fetch published kind 30023 articles, optionally filtered by author. */
export function useArticles(pubkey?: string) {
  const { nostr } = useNostr();

  return useQuery<NostrEvent[]>({
    queryKey: ['articles', pubkey ?? 'all'],
    queryFn: async (c) => {
      const filter: Record<string, unknown> = {
        kinds: [30023],
        limit: 50,
      };
      if (pubkey) {
        filter.authors = [pubkey];
      }
      const events = await nostr.query(
        [filter as Parameters<typeof nostr.query>[0][number]],
        { signal: c.signal },
      );
      return events.filter(isValidArticle);
    },
    staleTime: 60_000,
  });
}

/** Fetch a single article by author + d-tag identifier */
export function useArticle(pubkey: string, identifier: string) {
  const { nostr } = useNostr();

  return useQuery<NostrEvent | null>({
    queryKey: ['article', pubkey, identifier],
    queryFn: async (c) => {
      const [event] = await nostr.query(
        [{ kinds: [30023], authors: [pubkey], '#d': [identifier], limit: 1 }],
        { signal: c.signal },
      );
      return event ?? null;
    },
    staleTime: 60_000,
  });
}

/** Validate that an article event has the essential tags. */
function isValidArticle(event: NostrEvent): boolean {
  if (event.kind !== 30023) return false;
  const d = event.tags.find(([n]) => n === 'd')?.[1];
  const title = event.tags.find(([n]) => n === 'title')?.[1];
  return Boolean(d && title);
}

/** Extract metadata from a kind 30023 event */
export function getArticleMeta(event: NostrEvent) {
  const title = event.tags.find(([n]) => n === 'title')?.[1] ?? 'Untitled';
  const image = event.tags.find(([n]) => n === 'image')?.[1];
  const summary = event.tags.find(([n]) => n === 'summary')?.[1];
  const publishedAt = event.tags.find(([n]) => n === 'published_at')?.[1];
  const dTag = event.tags.find(([n]) => n === 'd')?.[1] ?? '';
  const hashtags = event.tags.filter(([n]) => n === 't').map(([, v]) => v);

  return {
    title,
    image,
    summary,
    publishedAt: publishedAt ? parseInt(publishedAt, 10) : event.created_at,
    dTag,
    hashtags,
  };
}
